import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  LinearProgress,
  Checkbox,
  Menu,
  MenuItem,
  Badge,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import {
  AvatarGroup
} from '@material-ui/lab';
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Comment as CommentIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon
} from '@material-ui/icons';
import { format, parseISO, isSameDay, isAfter, isBefore } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import useKanbanSubtasks from '../../hooks/useKanbanSubtasks';
import { toast } from 'react-toastify';
import TicketTasksModal from './TicketTasksModal';

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(0.8),
    background: theme.palette.type === 'dark' ? theme.palette.grey[800] : '#fff',
    borderRadius: 8,
    boxShadow: theme.palette.type === 'dark' 
      ? '0px 10px 17px -10px rgba(0, 0, 0, 0.8)' 
      : '0px 10px 17px -10px rgba(0, 0, 0, 0.59)',
    marginBottom: theme.spacing(1),
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.primary,
    '&:hover': {
      boxShadow: theme.palette.type === 'dark'
        ? '0px 15px 25px -5px rgba(0, 0, 0, 0.9)'
        : '0px 15px 25px -5px rgba(0, 0, 0, 0.3)',
      transform: 'translateY(-2px)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    justifyContent: 'space-between',
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  ticketNumber: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  divider: {
    background: theme.palette.divider,
  },
  lastMessageTime: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  lastMessageTimeUnread: {
    fontSize: '0.8rem',
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  cardDescription: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  valueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  descriptionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
  },
  cardButton: {
    fontSize: '0.5rem',
    padding: '2px 6px',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  connectionTag: {
    background: theme.palette.type === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '2px 6px',
    fontWeight: 'bold',
    borderRadius: '10px',
    fontSize: '0.5rem',
    marginLeft: 'auto',
  },
  opportunityValue: {
    fontSize: '0.9rem',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  removeValueButton: {
    padding: 0,
    marginLeft: theme.spacing(1),
    color: theme.palette.error.main,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
  dialogPaper: {
    borderRadius: '10px',
  },
  dialogButton: {
    borderRadius: '10px',
  },
  selectedCard: {
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
  },
  subtaskSummary: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  priorityBadge: {
    fontSize: '0.7rem',
    height: 16,
  },
  assigneeAvatar: {
    width: 20,
    height: 20,
    fontSize: '0.7rem',
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    marginTop: theme.spacing(0.5),
  },
  timeChip: {
    height: 20,
    fontSize: '0.6rem',
  },
  actionButton: {
    padding: 2,
    marginLeft: theme.spacing(0.5),
  },
  expandableSection: {
    marginTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
  },
}));

const KanbanCard = ({ 
  ticket, 
  index, 
  updateTicket, 
  isSelected, 
  onSelect, 
  showCheckbox = false 
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [tasksModalOpen, setTasksModalOpen] = useState(false);
  
  const { 
    subtasks: subtaskList, 
    fetchSubtasks, 
    createSubtask, 
    toggleSubtaskCompletion 
  } = useKanbanSubtasks(ticket.id);
  
  // Enhanced task data
  const taskData = {
    id: ticket.id,
    priority: ticket.priority || 'medium',
    status: ticket.status || 'todo',
    dueDate: ticket.dueDate,
    assignees: ticket.assignees || [],
    progress: ticket.progress || 0,
    timeTracked: ticket.timeTracked || 0,
    comments: ticket.comments || [],
    attachments: ticket.attachments || [],
    ...ticket
  };
  
  useEffect(() => {
    if (showSubtasks) {
      fetchSubtasks();
    }
  }, [showSubtasks, fetchSubtasks]);
  
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);
  

  const handleCardClick = () => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenTasksModal = (e) => {
    e.stopPropagation();
    setTasksModalOpen(true);
  };

  const handleCloseTasksModal = () => {
    setTasksModalOpen(false);
  };

  const lastMessageTimeClass =
    Number(ticket.unreadMessages) > 0
      ? classes.lastMessageTimeUnread
      : classes.lastMessageTime;

  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;

  const handleOpenModal = () => {
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const updateContactValue = async (contactId, value) => {
    try {
      await api.put(`/contacts/${contactId}`, {
        extraInfo: [{ name: 'valor', value: value.toString() }],
      });
    } catch (error) {
      console.error('Erro ao atualizar o valor:', error);
    }
  };

  const removeContactValue = async () => {
    try {
      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: [],
      });

      if (valueFieldIndex !== -1) {
        customFields.splice(valueFieldIndex, 1);
      }

      updateTicket({ ...ticket });

    } catch (error) {
      console.error('Erro ao remover o valor:', error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);

    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) {
        ticket.contact.extraInfo = [];
      }
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }

    updateTicket({ ...ticket });

    setOpen(false);
  };
  
  const handleAddSubtask = async () => {
    if (newSubtask.trim()) {
      try {
        await createSubtask({
          title: newSubtask.trim(),
          description: '',
          ticketId: ticket.id
        });
        setNewSubtask('');
        // O hook já atualiza a lista automaticamente
      } catch (error) {
        console.error('Error creating subtask:', error);
      }
    }
  };
  
  const handleToggleSubtask = async (subtaskId) => {
    try {
      const subtask = subtaskList.find(st => st.id === subtaskId);
      const newCompleted = subtask.status !== 'done';
      await toggleSubtaskCompletion(subtaskId, newCompleted);
    } catch (error) {
      console.error('Error toggling subtask:', error);
    }
  };
  
  const handleStartTimer = () => {
    setIsTimerRunning(true);
    // Here you would typically make an API call to start time tracking
  };
  
  const handleStopTimer = () => {
    setIsTimerRunning(false);
    // Here you would typically make an API call to stop time tracking
  };
  
  const handleMenuClick = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };
  
  const getPriorityColor = (priority) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800', 
      high: '#f44336',
      urgent: '#9c27b0'
    };
    return colors[priority] || colors.medium;
  };
  
  const isOverdue = taskData.dueDate && isAfter(new Date(), parseISO(taskData.dueDate));
  const isDueToday = taskData.dueDate && isSameDay(parseISO(taskData.dueDate), new Date());
  const isDueSoon = taskData.dueDate && !isDueToday && isBefore(new Date(), parseISO(taskData.dueDate));
  
  const completedSubtasks = subtaskList.filter(st => st.status === 'done').length;
  const progressPercent = subtaskList.length > 0 ? (completedSubtasks / subtaskList.length) * 100 : taskData.progress;

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={`${classes.card} ${isSelected ? classes.selectedCard : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            borderLeft: `4px solid ${getPriorityColor(taskData.priority)}`
          }}
        >
          {/* Header with checkbox, avatar, and actions */}
          <div className={classes.header}>
            <div className={classes.leftHeader}>
              {showCheckbox && (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect && onSelect(ticket.id, e.target.checked);
                  }}
                />
              )}
              <Avatar src={ticket.contact.urlPicture} className={classes.avatar} />
              <Box>
                <Tooltip title={ticket.contact.name}>
                  <Typography className={classes.cardTitle}>
                    {ticket.contact.name?.substring(0, 10) || ' '}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" color="textSecondary">
                  #{ticket.id}
                </Typography>
              </Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* Priority badge */}
              <Chip 
                size="small" 
                label={i18n.t(`kanban.tasks.priority.${taskData.priority}`)}
                style={{ 
                  backgroundColor: getPriorityColor(taskData.priority),
                  color: 'white',
                  fontSize: '0.7rem',
                  marginRight: 4
                }}
              />
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
          
          {/* Due date warning */}
          {isOverdue && (
            <Chip
              size="small"
              label={i18n.t('kanban.card.overdue')}
              color="secondary"
              icon={<ScheduleIcon />}
              style={{ marginBottom: 8, fontSize: '0.7rem' }}
            />
          )}
          {isDueToday && (
            <Chip
              size="small"
              label={i18n.t('kanban.card.dueToday')}
              style={{ backgroundColor: '#ff9800', color: 'white', marginBottom: 8, fontSize: '0.7rem' }}
              icon={<ScheduleIcon />}
            />
          )}
          
          {/* Progress bar */}
          {progressPercent > 0 && (
            <Box marginBottom={1}>
              <Typography variant="caption">
                {i18n.t('kanban.card.progress')}: {Math.round(progressPercent)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progressPercent} 
                style={{ borderRadius: 2, height: 4 }}
              />
            </Box>
          )}
          
          {/* Assignees */}
          {taskData.assignees.length > 0 && (
            <Box marginBottom={1}>
              <AvatarGroup max={3} style={{ justifyContent: 'flex-start' }}>
                {taskData.assignees.map(assignee => (
                  <Tooltip key={assignee.id} title={assignee.name}>
                    <Avatar 
                      src={assignee.profileImage} 
                      style={{ width: 24, height: 24, fontSize: '0.8rem' }}
                    >
                      {assignee.name.charAt(0)}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            </Box>
          )}
          {/* Subtasks summary */}
          {subtaskList.length > 0 && (
            <Box marginBottom={1}>
              <Typography variant="caption" className={classes.subtaskSummary}>
                <AssignmentIcon style={{ fontSize: '0.9rem', verticalAlign: 'middle', marginRight: 2 }} />
                {completedSubtasks}/{subtaskList.length} {i18n.t('kanban.tasks.subtask')}
              </Typography>
            </Box>
          )}
          
          <Divider className={classes.divider} />
          <div className={classes.valueRow}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                className={classes.opportunityValue}
                onClick={handleOpenModal}
              >
                {opportunityValue !== null
                  ? `Valor: $ ${opportunityValue.toFixed(2)}`
                  : 'Atribuir Valor'}
              </Typography>
              {opportunityValue !== null && (
                <Tooltip title={"Remover"}>
                  <IconButton
                    className={classes.removeValueButton}
                    onClick={removeContactValue}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
          <div className={classes.descriptionRow}>
            <Tooltip title={ticket.lastMessage || ' '}>
              <Typography className={classes.cardDescription}>
                {(() => {
                  const msg = ticket.lastMessage || '';
                  // Substituir emojis de mídia por texto mais curto para o card
                  if (msg.startsWith('🖼️')) return '📷 Imagem';
                  if (msg.startsWith('🎵')) return '🎵 Áudio';
                  if (msg.startsWith('🎥')) return '🎥 Vídeo';
                  if (msg.startsWith('📄') || msg.startsWith('📂') || msg.startsWith('📎')) return '📄 Documento';
                  if (msg.startsWith('🎭')) return '🎭 Figurinha';
                  if (msg.includes('BEGIN:VCARD')) return '👤 Contato';
                  if (msg.includes('data:image/png;base64')) return '📍 Localização';
                  return msg.substring(0, 20);
                })()}
              </Typography>
            </Tooltip>
            <Typography className={lastMessageTimeClass}>
              {isSameDay(parseISO(ticket.updatedAt), new Date())
                ? format(parseISO(ticket.updatedAt), 'HH:mm')
                : format(parseISO(ticket.updatedAt), 'dd/MM/yyyy')}
            </Typography>
          </div>
          {/* Footer with actions and metadata */}
          <div className={classes.footer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                size="small"
                className={classes.cardButton}
                onClick={handleOpenTasksModal}
                startIcon={<AssignmentIcon style={{ fontSize: '0.7rem' }} />}
              >
                {i18n.t('kanban.card.viewTasks')}
              </Button>
              <Button
                size="small"
                className={classes.cardButton}
                onClick={handleCardClick}
              >
                {i18n.t('kanban.card.viewTicket')}
              </Button>
              
              {/* Comments badge */}
              {taskData.comments.length > 0 && (
                <Badge badgeContent={taskData.comments.length} color="primary">
                  <CommentIcon style={{ fontSize: '1rem' }} />
                </Badge>
              )}
              
              {/* Time tracking */}
              {taskData.timeTracked > 0 && (
                <Tooltip title={`${Math.floor(taskData.timeTracked / 3600)}h ${Math.floor((taskData.timeTracked % 3600) / 60)}m`}>
                  <Chip 
                    size="small" 
                    icon={<ScheduleIcon />}
                    label={`${Math.floor(taskData.timeTracked / 3600)}h`}
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </div>
            
            {ticket.user && (
              <Typography className={classes.connectionTag}>
                {ticket.user.name.toUpperCase()}
              </Typography>
            )}
          </div>
          
          {/* Expandable subtasks section */}
          {subtaskList.length > 0 && (
            <Accordion 
              expanded={showSubtasks} 
              onChange={() => setShowSubtasks(!showSubtasks)}
              style={{ boxShadow: 'none', margin: 0 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 'auto', padding: 0 }}>
                <Typography variant="caption">
                  {i18n.t('kanban.tasks.subtask')} ({subtaskList.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ padding: '8px 0', display: 'block' }}>
                {subtaskList.map(subtask => (
                  <div key={subtask.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <Checkbox
                      size="small"
                      checked={subtask.status === 'done'}
                      onChange={() => handleToggleSubtask(subtask.id)}
                    />
                    <Typography 
                      variant="caption" 
                      style={{ 
                        textDecoration: subtask.completed ? 'line-through' : 'none',
                        opacity: subtask.completed ? 0.6 : 1
                      }}
                    >
                      {subtask.title}
                    </Typography>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                  <TextField
                    size="small"
                    placeholder={i18n.t('kanban.tasks.createSubtask')}
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddSubtask();
                      }
                    }}
                    style={{ flex: 1, marginRight: 8 }}
                  />
                  <IconButton size="small" onClick={handleAddSubtask}>
                    <AddIcon />
                  </IconButton>
                </div>
              </AccordionDetails>
            </Accordion>
          )}
          
          {/* Context Menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { setShowSubtasks(!showSubtasks); handleMenuClose(); }}>
              <AssignmentIcon style={{ marginRight: 8 }} />
              {showSubtasks ? i18n.t('kanban.hideSubtasks') : i18n.t('kanban.showSubtasks')}
            </MenuItem>
            <MenuItem onClick={() => { isTimerRunning ? handleStopTimer() : handleStartTimer(); handleMenuClose(); }}>
              {isTimerRunning ? <StopIcon style={{ marginRight: 8 }} /> : <PlayIcon style={{ marginRight: 8 }} />}
              {isTimerRunning ? i18n.t('kanban.tasks.stopTimer') : i18n.t('kanban.tasks.startTimer')}
            </MenuItem>
            <MenuItem onClick={() => { handleOpenModal(); handleMenuClose(); }}>
              <FlagIcon style={{ marginRight: 8 }} />
              {i18n.t('kanban.card.editValue')}
            </MenuItem>
          </Menu>
          <Dialog
            open={open}
            onClose={handleCloseModal}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>
              {valueField ? i18n.t('kanban.card.editValue') : i18n.t('kanban.card.assignValue')}
            </DialogTitle>
            <DialogContent>
              <TextField
                label={i18n.t('kanban.card.value')}
                type="number"
                fullWidth
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.textField}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="secondary"
                variant="outlined"
                className={classes.dialogButton}
              >
                {i18n.t('kanban.cancel')}
              </Button>
              <Button
                onClick={handleSaveValue}
                color="primary"
                variant="outlined"
                className={classes.dialogButton}
              >
                {i18n.t('kanban.save')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Tasks Modal */}
          <TicketTasksModal
            open={tasksModalOpen}
            onClose={handleCloseTasksModal}
            ticketId={ticket.id}
            ticketName={ticket.contact.name}
          />
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
